import * as Api from '@ViewModels';
import { css } from 'aphrodite';
import { inject, observer } from 'mobx-react';
import moment from 'moment';
import * as React from 'react';
import { Modifier } from 'react-day-picker-old/types/common';
import { IImpersonationContextComponentProps, ImpersonationContextKey } from '../../../../models';
import { useUserSession } from '../../../../models/hooks/appStateHooks';
import {
	accountHasComplianceEnabled,
	getComplianceDisabledDays,
	getScheduleTimeOptions,
	getSendOnBehalfDisabledDays,
	getSendOnBehalfWaitTime,
} from '../../../../models/UiUtils';
import { Button } from '../../../components-v2/Button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../../components-v2/Select';
import { bs } from '../../../styles/styles';
import { CampaignDayPicker } from '../../CampaignDayPicker';
import { Separator } from '../../Separator';
import { styleSheet } from './styles';

export const CampaignDateTimePicker = inject(ImpersonationContextKey)(
	observer(function CampaignDateTimePicker({
		initialValue,
		onSave,
		onCancel,
		expirationDate,
		impersonationContext,
		sendFrom,
		sendFromUserId,
		ctaText,
		ignoreDisabledDays = false,
		ignoreEmailTimeIntervals = false,
	}: {
		initialValue: Date;
		onSave(newValue: Date): void;
		onCancel(): void;
		expirationDate?: Date;
		sendFrom?: Api.ISendEmailFrom;
		sendFromUserId?: string;
		ctaText?: React.ReactNode;
		// Ultimately would probably make sense to have a social version of this whole component with email aspects taken out.
		// Meantime while social still uses this component, the ignore props below can be used to opt-out of some email-specific date restrictions.
		ignoreDisabledDays?: boolean;
		ignoreEmailTimeIntervals?: boolean;
	} & IImpersonationContextComponentProps) {
		const userSession = useUserSession();
		const [value, setValue] = React.useState<Date>(initialValue);
		const [selectedHour, setSelectedHour] = React.useState<number>(initialValue ? initialValue.getHours() : null);

		const requiresCompliance = accountHasComplianceEnabled(userSession, impersonationContext);

		const minStartDate = React.useMemo(() => {
			if (!sendFrom) {
				return new Date();
			}
			const currentUserId = impersonationContext?.isValid ? impersonationContext?.user?.id : userSession.user.id;
			const senderIsNotCurrentUser =
				Api.SendEmailFrom.ContactOwner === sendFrom ||
				(Api.SendEmailFrom.SelectedUser === sendFrom && sendFromUserId !== currentUserId);
			return requiresCompliance && senderIsNotCurrentUser
				? moment
						.max([
							moment(getComplianceDisabledDays(userSession, impersonationContext, false)),
							moment(getSendOnBehalfWaitTime(userSession, impersonationContext)),
						])
						.toDate()
				: requiresCompliance
					? getComplianceDisabledDays(userSession, impersonationContext, false)
					: senderIsNotCurrentUser
						? getSendOnBehalfDisabledDays(userSession, impersonationContext)
						: null;
			// eslint-disable-next-line react-hooks/exhaustive-deps
		}, []);

		const onTimeChanged = (hour: number) => {
			setSelectedHour(hour);
			value.setHours(hour);
			setValue(value);
		};

		const timeOptions = getScheduleTimeOptions(
			0,
			userSession,
			undefined,
			moment(value),
			impersonationContext,
			ignoreEmailTimeIntervals
		).map(o => ({
			label: o.title as string,
			value: String(o.value),
		}));
		const NUM_WEEKS_SHOWN_IN_CAL = 6;

		const disabledDays = expirationDate
			? [{ after: expirationDate, before: minStartDate ? minStartDate : new Date() }]
			: [{ before: minStartDate ? minStartDate : new Date() }];

		const businessHours =
			impersonationContext?.account?.features?.sendEmail || userSession?.account?.features?.sendEmail;

		const outsideBusinessHourDays: Modifier[] = [];
		if (businessHours?.observeSendIntervals && ignoreDisabledDays === false) {
			if (!businessHours?.saturdayInterval?.startMinutes && !businessHours?.saturdayInterval?.endMinutes) {
				outsideBusinessHourDays.push({ daysOfWeek: [6] });
			}
			if (!businessHours?.sundayInterval?.startMinutes && !businessHours?.sundayInterval?.endMinutes) {
				outsideBusinessHourDays.push({ daysOfWeek: [0] });
			}
			if (!businessHours?.fridayInterval?.startMinutes && !businessHours?.fridayInterval?.endMinutes) {
				outsideBusinessHourDays.push({ daysOfWeek: [5] });
			}
		}

		const onDaySelected = (day: Date) => {
			day.setHours(value.getHours());
			setValue(day);
		};

		return (
			<div className={css(styleSheet.DateTimePicker)}>
				<div className={css(styleSheet.body)}>
					<CampaignDayPicker
						selectedDay={value}
						disabledDays={[...disabledDays, ...outsideBusinessHourDays]}
						numWeeksShown={NUM_WEEKS_SHOWN_IN_CAL}
						onDayClick={onDaySelected}
						modifiers={null}
						styles={[bs.m0, bs.wFull]}
					/>
					<Separator />
					<Select
						items={timeOptions}
						value={selectedHour.toString()}
						onValueChange={newTime => onTimeChanged(parseInt(newTime))}
					>
						<SelectTrigger styles={[bs.w32]}>
							<SelectValue placeholder='Select a time...' />
						</SelectTrigger>
						<SelectContent>
							{timeOptions.map(o => {
								return (
									<SelectItem key={o.value} value={o.value.toString()}>
										{o.label as string}
									</SelectItem>
								);
							})}
						</SelectContent>
					</Select>
					<Separator />
				</div>
				<div className={css(styleSheet.footer)}>
					<Button onClick={() => onSave(value)}>{ctaText ? ctaText : 'Schedule'}</Button>
					<Button variant='secondary' onClick={onCancel}>
						Cancel
					</Button>
				</div>
			</div>
		);
	})
);
